<template>
  <iColumn
    v-if="editMode"
    :width="getOptionValue(LayoutWidth)"
    :gap="getOptionValue(LayoutGap)"
    :style-overrides="spacingStyleOverrides"
  >
    <iRow
      v-for="(item, idx) in placeholderItems"
      :key="idx"
      gap="small"
      vertical-align="middle"
      width="fill"
      :style-overrides="{
        paddingTop: 'small',
        paddingBottom: 'small',
        paddingLeft: 'small',
        paddingRight: 'small',
        borderRadius: 'standard',
        backgroundColor: '#f4f5f7',
      }"
    >
      <iColumn gap="extraSmall" width="fill">
        <iText font-weight="strong">
          {{ item.title }}
        </iText>
        <iText variant="subtle" font-size="small">
          {{ item.subtitle }}
        </iText>
      </iColumn>
    </iRow>
  </iColumn>
  <iColumn
    v-else
    :width="getOptionValue(LayoutWidth)"
    :style-overrides="spacingStyleOverrides"
  >
    <SmartFeed
      :source="folderContentsFeedStore"
      :default-feed-layout="getOptionValue(SmartFeedLayout)"
      :default-entity-size="getOptionValue(SmartFeedCardLayout)"
      :default-filter-values="defaultFilterValues"
      :gap="getOptionValue(LayoutGap)"
      :edit-mode="editMode"
      @click:item="goToStory"
    >
      <template #item="{feedItem}">
        <iColumn
          v-if="cardDcForItem(feedItem) > 0"
          class="cursor-pointer"
          width="fill"
          @click.capture="goToStory(feedItem)"
        >
          <StudioFeedRowItem
            :dynamic-component-id="cardDcForItem(feedItem)"
            :entity-id="feedItem.id"
            context-name="story"
            :theme-mode="themeMode"
            :edit-mode="editMode"
          />
        </iColumn>
        <iRow
          v-else
          class="cursor-pointer"
          gap="small"
          vertical-align="middle"
          width="fill"
          :style-overrides="{
            paddingTop: 'small',
            paddingBottom: 'small',
            paddingLeft: 'small',
            paddingRight: 'small',
            borderRadius: 'standard',
            backgroundColor: '#f4f5f7',
          }"
          @click="goToStory(feedItem)"
        >
          <iText font-weight="strong" :min-width="0">
            {{ storyTitleForItem(feedItem) }}
          </iText>
        </iRow>
      </template>
      <template #empty-state>
        <iText variant="subtle">
          {{ getOptionValue(EmptyStateText) }}
        </iText>
      </template>
    </SmartFeed>
  </iColumn>
</template>

<script>
import SmartFeed from "SmartFeed/components/SmartFeedV2.vue";
import StudioFeedRowItem from "%/dynamic-component-editor/standard-elements/StudioFeedRowItem.vue";
import SmartFeedEngine from "@studio/StoriesEditor/smart-feed/smart-feed-engine";
import { StoryPageElementMixin } from "%/dynamic-component-editor/standard-pages/story-page/story-page-element-mixin";
import { SmartFeedElementMixin } from "%/dynamic-component-editor/mixins/smart-feed-element-mixin";
import LayoutWidth from "%/dynamic-component-editor/options/layout-width";
import LayoutGap from "%/dynamic-component-editor/options/layout-gap";
import EmptyStateText from "%/dynamic-component-editor/options/empty-state-text";
import Padding from "%/dynamic-component-editor/options/padding";
import Margins from "%/dynamic-component-editor/options/margins";
import SpacingOptions from "%/dynamic-component-editor/bundles/spacing-options";
import { useConfigurationStore } from "%/dynamic-component-editor/stores/configuration-store";
import { useCompletionAssignmentStore } from "%/dynamic-component-editor/stores/completion-assignment-store";
import { useCompletionStatusStore } from "%/dynamic-component-editor/stores/completion-status-store";
import { resolveCompletionStatus }
  from "%/dynamic-component-editor/helpers/completion-status-resolver";
import { ELEMENT_PANEL_FOLDER } from "%/dynamic-component-editor/constants/element-panel-folder-names";

export default {
  name: "FolderContents",
  components: {
    SmartFeed,
    StudioFeedRowItem,
  },
  mixins: [
    StoryPageElementMixin,
    SmartFeedElementMixin,
  ],
  elementPanelSettings: {
    folder: ELEMENT_PANEL_FOLDER.FOLDERS,
    displayName: "Folder Contents",
    displayIcon: "feed",
    elementGroup: "storyElements",
    elementType: "element",
  },
  // Deprecated — superseded by the FeedV2 "Folder Feed" element
  // (StoryFolderFeedElement), whose card control opens the shared Story
  // Component Picker modal. `deprecated` removes it from the author palette so
  // no NEW usage is added; kept registered so already-authored pages still
  // render. Full removal (delete this component + migrate existing pages/specs
  // off it) is a follow-up pending a usage audit.
  deprecated: true,
  data() {
    return {
      LayoutWidth,
      LayoutGap,
    };
  },
  optionBundles: [
    SpacingOptions,
  ],
  options: [
    new LayoutWidth({
      "default": "fill",
      "hover": "fill",
    }, true),
    LayoutGap,
    new EmptyStateText({
      "default": "There are no stories in this folder yet.",
    }),
  ],
  computed: {
    folderContentsFeedStore() {
      return SmartFeedEngine.getFeedPiniaStore("FolderContentsFeedSource");
    },
    defaultFilterValues() {
      return {
        folder_story_id: this.storyEntityId,
      };
    },
    spacingStyleOverrides() {
      const padding = this.getOptionValue(Padding) || {};
      const margins = this.getOptionValue(Margins) || {};
      const overrides = {};
      if (padding.top) {
        overrides.paddingTop = padding.top;
      }
      if (padding.right) {
        overrides.paddingRight = padding.right;
      }
      if (padding.bottom) {
        overrides.paddingBottom = padding.bottom;
      }
      if (padding.left) {
        overrides.paddingLeft = padding.left;
      }
      if (margins.top) {
        overrides.marginTop = margins.top;
      }
      if (margins.right) {
        overrides.marginRight = margins.right;
      }
      if (margins.bottom) {
        overrides.marginBottom = margins.bottom;
      }
      if (margins.left) {
        overrides.marginLeft = margins.left;
      }
      return overrides;
    },
    placeholderItems() {
      return [
        { title: "First story in folder", subtitle: "Story in this folder" },
        { title: "Second story in folder", subtitle: "Story in this folder" },
        { title: "Third story in folder", subtitle: "Story in this folder" },
      ];
    },
    completionPublicationId() {
      return useConfigurationStore().publicationId;
    },
  },
  watch: {
    completionPublicationId: {
      immediate: true,
      handler(publicationId) {
        if (!this.editMode && publicationId) {
          useCompletionAssignmentStore().ensureSubVariantAssignments(publicationId);
          useCompletionAssignmentStore().ensureTwoStateMode(publicationId);
        }
      },
    },
  },
  methods: {
    cardDcForItem(feedItem) {
      try {
        const entity = SmartFeedEngine.getEntityPiniaStore(feedItem.type)().getEntity(feedItem.id);
        const baseCardId = Number(entity?.cardDynamicComponentId) || 0;
        if (baseCardId <= 0 || this.editMode) {
          return baseCardId;
        }
        const completion = useCompletionStatusStore();
        completion.rememberParentFolder(feedItem.id, this.storyEntityId);
        const rawStatus = resolveCompletionStatus({
          entity,
          storyId: feedItem.id,
          statusByStory: completion.statusByStory,
          progressByFolder: completion.progressByFolder,
        });
        const status = useCompletionAssignmentStore().normalizeStatus(this.completionPublicationId, rawStatus);
        const subVariantId = useCompletionAssignmentStore().resolveSubVariantId(
          this.completionPublicationId,
          baseCardId,
          status,
        );
        return subVariantId > 0 ? subVariantId : baseCardId;
      } catch {
        return 0;
      }
    },
    storyTitleForItem(feedItem) {
      try {
        const entity = SmartFeedEngine.getEntityPiniaStore(feedItem.type)().getEntity(feedItem.id);
        return entity?.storyTitle || entity?.title || "Untitled story";
      } catch {
        return "Untitled story";
      }
    },
    goToStory(feedItem) {
      const entity = SmartFeedEngine.getEntityPiniaStore(feedItem.type)().getEntity(feedItem.id);
      const path = entity && entity.nestedUrl ? entity.nestedUrl : `/story/${feedItem.id}`;
      window.location.href = this.links.web + path;
    },
  },
};
</script>
